import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Minus, Plus, X } from 'react-feather';

import apiToken, { handleApiError } from '@/shared/services/axiosInstance';
import Button from '@/shared/components/UI/Button';
import LinkButton from '@/shared/components/UI/LinkButton';
import {
  getTotalPrice,
  formatPrice,
  roundPrice,
} from '@/shared/utils/priceUtils';
import { formatDateYYYYMMDD } from '@/shared/utils/dateUtils';
import { useCartStore } from '@/shared/stores/useCartStore';

import { useCartUpdates } from '../hooks/useCartUpdates';
import { useQuantityValidation } from '../hooks/useCartQuantityValidation';
import { useStockAdjustment } from '../hooks/useStockAdjustment';
import { CartData, CartResponse } from '../types/CartTypes';
import CartEmpty from './CartEmpty';
import { useQuantityInput } from '../hooks/useCartQuantityInput';

const Cart = () => {
  const [cartData, setCartData] = useState<CartData | null>(null);
  const [totalPrice, setTotalPrice] = useState<number>(0);
  const [currencySymbol, setCurrencySymbol] = useState<string>('원');
  const { debouncedUpdateCart, cancelPendingUpdate } = useCartUpdates();
  const {
    validateQuantity,
    canIncreaseQuantity,
    canDecreaseQuantity,
    getItemsBelowMOQ,
  } = useQuantityValidation();
  const { adjustCartForStock } = useStockAdjustment();

  const isEmpty = !cartData || cartData.cartList.length === 0;

  const updateLocalQuantity = (uuid: string, newQuantity: number) => {
    if (!cartData) return;

    const updatedCartList = cartData.cartList.map((item) => {
      if (item.uuid === uuid) {
        return { ...item, quantity: newQuantity };
      }
      return item;
    });

    const newTotal = updatedCartList.reduce(
      (sum, item) => sum + item.productPrice.price * item.quantity,
      0,
    );

    setCartData({ ...cartData, cartList: updatedCartList });
    setTotalPrice(newTotal);
  };

  const { handleInputChange, getInputValue, resetInputValue } =
    useQuantityInput(cartData?.cartList ?? [], {
      onQuantityChange: (uuid, quantity) => {
        const current = cartData?.cartList.find(
          (item) => item.uuid === uuid,
        )?.quantity;

        if (current === quantity) return;

        updateLocalQuantity(uuid, quantity);

        cancelPendingUpdate();
        debouncedUpdateCart(
          {
            cartUuid: uuid,
            quantity,
          },
          300,
        );
      },
      onValidationError: (message) => {
        alert(message);
      },
      debounceDelay: 1000,
    });

  const handleQuantityChange = async (uuid: string, delta: number) => {
    if (!cartData) return;

    const item = cartData.cartList.find((option) => option.uuid === uuid);
    if (!item) return;

    const newQuantity = item.quantity + delta;
    const validation = validateQuantity(item, newQuantity);

    if (!validation.isValid && validation.message) {
      alert(validation.message);
      return;
    }

    const finalQuantity = validation.validQuantity;

    updateLocalQuantity(uuid, finalQuantity);
    resetInputValue(uuid, finalQuantity);

    // Debounced API call
    debouncedUpdateCart({
      cartUuid: uuid,
      quantity: finalQuantity,
    });
  };

  const handleRemoveItem = async (uuid: string) => {
    if (!cartData) return;

    try {
      await apiToken.delete(`/api/v1/cart`, { data: { cartUuid: uuid } });

      const updatedCartList = cartData.cartList.filter(
        (item) => item.uuid !== uuid,
      );
      const newTotal = updatedCartList.reduce(
        (sum, item) => sum + item.productPrice.price * item.quantity,
        0,
      );

      setCartData({ ...cartData, cartList: updatedCartList });
      setTotalPrice(newTotal);
    } catch (error) {
      const errorMessage = handleApiError(error);
      alert(errorMessage);
    }
  };

  const getCartItem = useCallback(async () => {
    try {
      const response = await apiToken.get<CartResponse>('/api/v1/cart');
      const { data } = response.data;

      // Check for items below MOQ and delete them first
      const itemsBelowMOQ = getItemsBelowMOQ(data.cartList);
      let filteredCartList = data.cartList;

      if (itemsBelowMOQ.length > 0) {
        // Delete items below MOQ from server
        await Promise.all(
          itemsBelowMOQ.map(async (item) => {
            try {
              await apiToken.delete(`/api/v1/cart`, {
                data: { cartUuid: item.uuid },
              });
            } catch (error) {
              const errorMessage = handleApiError(error);
              console.error(
                'Failed to delete cart item below MOQ:',
                errorMessage,
              );
            }
          }),
        );

        // Filter out deleted items
        filteredCartList = data.cartList.filter(
          (item) =>
            !itemsBelowMOQ.some(
              (belowMOQItem) => belowMOQItem.uuid === item.uuid,
            ),
        );
      }

      const { adjustedCartList, itemsToUpdate, itemsToDelete } =
        adjustCartForStock(filteredCartList);

      if (itemsToDelete.length > 0) {
        await Promise.all(
          itemsToDelete.map(async (uuid) => {
            try {
              await apiToken.delete(`/api/v1/cart`, {
                data: { cartUuid: uuid },
              });
            } catch (error) {
              const errorMessage = handleApiError(error);
              console.error('Failed to delete cart item:', errorMessage);
            }
          }),
        );
      }

      if (itemsToUpdate.length > 0) {
        await Promise.all(
          itemsToUpdate.map(async ({ cartUuid, quantity }) => {
            try {
              await apiToken.patch('/api/v1/cart', { cartUuid, quantity });
            } catch (error) {
              const errorMessage = handleApiError(error);
              console.error('Failed to update cart quantity:', errorMessage);
            }
          }),
        );
      }

      // Update local state with adjusted quantities
      const adjustedData = {
        ...data,
        cartList: adjustedCartList,
        totalPrice: roundPrice(
          adjustedCartList.reduce(
            (sum, item) => sum + item.productPrice.price * item.quantity,
            0,
          ),
          data.currencyCode,
        ),
      };

      useCartStore.getState().setValidatedCartData(adjustedData);

      setCartData(adjustedData);
      setTotalPrice(adjustedData.totalPrice);
      setCurrencySymbol(data.currencySymbol);

      const totalDeleted = itemsBelowMOQ.length + itemsToDelete.length;
      const totalAdjusted = itemsToUpdate.length;

      if (totalDeleted > 0 && totalAdjusted > 0) {
        alert(
          'Your cart has been automatically updated based on the latest stock and ordering conditions.\n' +
            'Please review the updated items and quantities.',
        );
      } else if (totalDeleted > 0) {
        alert(
          'Some items have been automatically removed from your cart due to being out of stock or below the minimum order quantity.\n' +
            'Please review your updated cart.',
        );
      } else if (totalAdjusted > 0) {
        alert(
          'Some item quantities have been automatically adjusted based on stock availability.\n' +
            'Please double-check the updated quantities.',
        );
      }
    } catch (error: any) {
      const errorMessage = handleApiError(error);
      console.error('Failed to fetch cart items:', errorMessage);
      alert(errorMessage);
    }
  }, [adjustCartForStock, getItemsBelowMOQ]);

  useEffect(() => {
    getCartItem();
  }, [getCartItem]);

  if (isEmpty) {
    return <CartEmpty />;
  }

  return (
    <div className="mx-auto mb-[100px] mt-[68px] w-[53.25rem]">
      <h3 className="text-center text-2xl font-extrabold tracking-[0.1rem] text-[#95699f]">
        CART
      </h3>

      <hr className="my-6 w-full border border-[#ddd]" />

      {cartData!.cartList.map((item) => {
        const optionText = item.productOption.optionNameValue2
          ? `${item.productOption.optionNameValue1} / ${item.productOption.optionNameValue2}`
          : item.productOption.optionNameValue1;

        const canIncrease = canIncreaseQuantity(item);
        const canDecrease = canDecreaseQuantity(item);
        const minQuantity = item.productOption.minimumOrderQuantity || 1;
        const maxQuantity =
          item.productOption.stock - item.productOption.holdStock;

        return (
          <div key={item.uuid}>
            <div className="mx-6 my-5 flex justify-between">
              <img
                src={item.product.thumbnailUrl}
                alt="product"
                className="h-28 w-28 rounded-lg"
              />

              <div className="w-full max-w-[42.25rem]">
                <div className="flex justify-between">
                  <p className="max-w-[36.25rem] text-base font-semibold leading-[140%] text-[#222]">
                    {item.product.title}
                  </p>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveItem(item.uuid)}
                    leftIcon={
                      <X className="h-5 w-5 text-[#222222]" strokeWidth={1.5} />
                    }
                    className="h-6 w-6 p-0"
                  />
                </div>

                <p className="mt-2 text-xs font-normal leading-[140%] text-[#666]">
                  {item.product.vendor}
                </p>

                <div className="mt-3 flex items-center justify-between">
                  <p className="text-base font-semibold leading-[140%] text-[#b995be]">
                    {optionText}
                  </p>

                  <div className="flex h-9 w-[132px] justify-between rounded border">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleQuantityChange(item.uuid, -1)}
                      disabled={!canDecrease}
                      leftIcon={<Minus className="h-4 w-4" />}
                      className="h-full w-[34px] rounded-none"
                    />
                    <input
                      type="text"
                      className="max-w-14 border-0 px-2 text-center outline-0"
                      value={getInputValue(item.uuid, item.quantity)}
                      min={minQuantity}
                      max={maxQuantity}
                      onChange={(e) =>
                        handleInputChange(item.uuid, e.target.value)
                      }
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleQuantityChange(item.uuid, 1)}
                      disabled={!canIncrease}
                      leftIcon={<Plus className="h-4 w-4" />}
                      className="h-full w-[34px] rounded-none hover:bg-transparent"
                    />
                  </div>
                </div>

                <div className="mt-1 flex items-center justify-between">
                  <div className="flex h-full items-center justify-center">
                    {item.product.releaseDate && (
                      <>
                        {' '}
                        <AlertTriangle className="mr-1 h-[14px] w-[14px] text-[#4a4a4a]" />
                        <p className="text-sm font-medium text-[#4a4a4a]">
                          Release Date :{' '}
                          {formatDateYYYYMMDD(item.product.releaseDate)}
                        </p>{' '}
                      </>
                    )}
                  </div>

                  <p className="text-xs font-normal text-[#999]">
                    MOQ: {minQuantity}
                  </p>
                </div>

                <div className="mt-4 flex items-center justify-between">
                  <p className="text-base font-semibold text-[#222]">
                    Subtotal
                  </p>
                  <span className="text-base font-semibold text-[#95699f]">
                    {formatPrice(
                      getTotalPrice(
                        item.productPrice.price,
                        item.quantity,
                        cartData?.currencyCode,
                      ),
                      cartData?.currencyCode,
                    )}
                    {currencySymbol}
                  </span>
                </div>
              </div>
            </div>
            <hr className="my-6 w-full border border-[#ddd]" />
          </div>
        );
      })}

      <div className="mx-5 my-8 flex justify-between">
        <p className="text-lg font-semibold text-[#222]">Total</p>
        <span className="text-lg font-semibold text-[#95699f]">
          {formatPrice(totalPrice, cartData?.currencyCode)}
          {currencySymbol}
        </span>
      </div>

      <hr className="my-6 w-full border border-[#ddd]" />

      <div className="flex flex-col items-center">
        <LinkButton
          to="/order"
          variant="primary"
          className="mt-7 h-10 w-72 px-6"
        >
          CHECKOUT
        </LinkButton>
        <LinkButton to="/" variant="text" className="mt-6 text-sm">
          CONTINUE SHOPPING
        </LinkButton>
      </div>
    </div>
  );
};

export default Cart;
